import { isProcessingMicrolearningString } from "./activeCheckpointMicrolearningValidity";
import {
  extractCloudflareStreamCustomerHost,
  extractCloudflareStreamVideoUidFromEmbeds,
  normalizeCloudflareStreamVideoUid,
} from "./simulatedCloudflareStreamWebhook";

export const VIDEO_POST_UPLOAD_REVEAL_MIN_MS = 10000;
export const VIDEO_POST_UPLOAD_REVEAL_MAX_MS = 15000;
export const VIDEO_POST_UPLOAD_REVEAL_POLL_MS = 2000;

export const VIDEO_POST_UPLOAD_TOAST_TITLE = "Video is being processed";
export const VIDEO_POST_UPLOAD_TOAST_MESSAGE = "The player will appear when your video is ready.";

export function isPlayableSignedVideoUrl(url) {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed || trimmed.includes("/None/")) return false;
  return !isProcessingMicrolearningString(trimmed);
}

async function resolveLatestSignedUrl(getSignedUrl, fallbackUrl) {
  if (typeof getSignedUrl !== "function") return fallbackUrl;
  try {
    const url = await getSignedUrl();
    return url || fallbackUrl;
  } catch {
    return fallbackUrl;
  }
}

/**
 * Keep the post-upload loader visible for at least `minMs`, poll until the signed URL is
 * playable, and stop at `maxMs` even if encoding is still running.
 */
export async function waitForVideoPostUploadReveal({
  getSignedUrl,
  minMs = VIDEO_POST_UPLOAD_REVEAL_MIN_MS,
  maxMs = VIDEO_POST_UPLOAD_REVEAL_MAX_MS,
  pollMs = VIDEO_POST_UPLOAD_REVEAL_POLL_MS,
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
} = {}) {
  const start = Date.now();
  const hasGetter = typeof getSignedUrl === "function";
  let lastUrl = null;

  while (Date.now() - start < maxMs) {
    const elapsed = Date.now() - start;
    lastUrl = await resolveLatestSignedUrl(getSignedUrl, lastUrl);
    const ready = hasGetter ? isPlayableSignedVideoUrl(lastUrl) : true;

    if (elapsed >= minMs && ready) {
      return { ready: true, signedUrl: lastUrl, elapsedMs: elapsed };
    }

    const remaining = maxMs - (Date.now() - start);
    if (remaining <= 0) break;
    await sleep(Math.min(pollMs, remaining));
  }

  return {
    ready: isPlayableSignedVideoUrl(lastUrl),
    signedUrl: lastUrl,
    elapsedMs: Date.now() - start,
  };
}

/**
 * Build the Cloudflare Stream DASH manifest URL — the file the player fetches to begin playback.
 * Returns 500 while the video is still encoding and 200 once it is ready to stream, so it can be
 * polled directly from the browser (it is the public playback URL, no API token required).
 * @param {string} customerHost e.g. `customer-xxxx.cloudflarestream.com` (scheme optional)
 * @param {string} uid Stream video uid
 * @param {string} [parentOrigin] e.g. `http://localhost:3000`
 * @returns {string}
 */
/**
 * Resolve the DASH manifest URL from a signed Stream URL, iframe HTML, or embed src.
 * Falls back to `videodelivery.net` when the customer subdomain is not in the string.
 */
export function resolveCloudflareStreamManifestUrl(source, parentOrigin) {
  const uid = extractCloudflareStreamVideoUidFromEmbeds(source);
  if (!uid) return null;
  const customerHost = extractCloudflareStreamCustomerHost(source);
  const host = customerHost || "videodelivery.net";
  const origin =
    parentOrigin ?? (typeof window !== "undefined" ? window.location?.origin : undefined);
  return cloudflareStreamManifestUrl(host, uid, origin);
}

export function cloudflareStreamManifestUrl(customerHost, uid, parentOrigin) {
  const host = String(customerHost ?? "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");
  const base = `https://${host}/${uid}/manifest/video.mpd`;
  return parentOrigin ? `${base}?parentOrigin=${encodeURIComponent(parentOrigin)}` : base;
}

/**
 * Poll the Cloudflare Stream manifest until it is reachable (HTTP 200), i.e. the video is ready
 * to play. Resolves as soon as it is ready; stops at `maxMs` otherwise. No fixed wait.
 *
 * @param {object} opts
 * @param {string} opts.manifestUrl Full manifest URL (see `cloudflareStreamManifestUrl`)
 * @param {(url: string) => Promise<boolean>} [opts.probe] Returns true when the manifest is ready
 * @returns {Promise<{ ready: boolean, elapsedMs: number }>}
 */
export async function pollCloudflareStreamManifestReady({
  manifestUrl,
  probe,
  maxMs = 10 * 60 * 1000,
  pollMs = 3000,
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
} = {}) {
  const check =
    typeof probe === "function"
      ? probe
      : async (url) => {
          try {
            const res = await fetch(url, { method: "GET", cache: "no-store" });
            return res.ok;
          } catch {
            return false;
          }
        };

  const start = Date.now();

  while (Date.now() - start < maxMs) {
    if (manifestUrl && (await check(manifestUrl))) {
      return { ready: true, elapsedMs: Date.now() - start };
    }
    const remaining = maxMs - (Date.now() - start);
    if (remaining <= 0) break;
    await sleep(Math.min(pollMs, remaining));
  }

  return { ready: false, elapsedMs: Date.now() - start };
}

/**
 * Poll the Stream manifest for a signed URL / embed until Cloudflare returns HTTP 200.
 * No-op (ready: true) when the source is not a Cloudflare Stream asset.
 */
export async function waitForCloudflareStreamManifestFromSource({
  source,
  parentOrigin,
  maxMs = 10 * 60 * 1000,
  pollMs = 3000,
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
} = {}) {
  const manifestUrl = resolveCloudflareStreamManifestUrl(source, parentOrigin);
  if (!manifestUrl) {
    return { ready: true, manifestUrl: null, elapsedMs: 0 };
  }
  const result = await pollCloudflareStreamManifestReady({
    manifestUrl,
    maxMs,
    pollMs,
    sleep,
  });
  return { ...result, manifestUrl };
}

/**
 * Build the Cloudflare Stream "video details" status URL.
 * @see https://developers.cloudflare.com/api/operations/stream-videos-retrieve-video-details
 * @param {string} accountId Cloudflare account id
 * @param {string} uid Stream video uid
 * @returns {string}
 */
export function cloudflareStreamStatusUrl(accountId, uid) {
  return `https://api.cloudflare.com/client/v4/accounts/${accountId}/stream/${uid}`;
}

/**
 * True once Cloudflare has finished encoding and the video can be streamed.
 * @param {object} [body] Parsed Cloudflare Stream details response (`{ result: { ... } }` or the result itself)
 */
export function isCloudflareStreamReady(body) {
  const result = body?.result ?? body;
  if (!result) return false;
  // Backend proxy uses snake_case (`ready_to_stream`); Cloudflare API uses camelCase.
  return (
    result.ready_to_stream === true ||
    result.readyToStream === true ||
    result?.status?.state === "ready"
  );
}

/**
 * Poll the Cloudflare Stream status endpoint until the video is ready to stream or `maxMs` elapses.
 *
 * NOTE: the Stream API requires an Authorization token, so this should hit a backend proxy
 * rather than `api.cloudflare.com` directly from the browser. Pass a `fetcher` that returns the
 * parsed JSON body for the given uid; default uses `fetch` against `cloudflareStreamStatusUrl`.
 *
 * @param {object} opts
 * @param {string} opts.uid Stream video uid
 * @param {string} [opts.accountId] Used only by the default fetcher
 * @param {(uid: string) => Promise<object>} [opts.fetcher] Returns parsed status body
 * @param {() => boolean} [opts.shouldContinue] Return false to stop polling (e.g. the video was removed)
 * @returns {Promise<{ ready: boolean, body: object|null, pctComplete: number|null, elapsedMs: number }>}
 */
function streamPctComplete(body, fallback) {
  const pct = Number(body?.result?.status?.pctComplete ?? body?.status?.pctComplete);
  return Number.isFinite(pct) ? pct : fallback;
}

async function loadStreamStatus(load, uid, fallbackBody) {
  if (!uid) return fallbackBody;
  try {
    return await load(uid);
  } catch {
    return fallbackBody;
  }
}

export async function pollCloudflareStreamStatus({
  uid,
  accountId,
  fetcher,
  maxMs = VIDEO_POST_UPLOAD_REVEAL_MAX_MS,
  pollMs = VIDEO_POST_UPLOAD_REVEAL_POLL_MS,
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  shouldContinue = () => true,
} = {}) {
  const videoUid = normalizeCloudflareStreamVideoUid(uid);
  if (!videoUid) return { ready: false, body: null, pctComplete: null, elapsedMs: 0 };

  const load =
    typeof fetcher === "function"
      ? fetcher
      : async (id) => {
          const res = await fetch(cloudflareStreamStatusUrl(accountId, id));
          return res.json();
        };

  const start = Date.now();
  let lastBody = null;

  while (Date.now() - start < maxMs && shouldContinue()) {
    lastBody = await loadStreamStatus(load, videoUid, lastBody);
    if (isCloudflareStreamReady(lastBody)) {
      return {
        ready: true,
        body: lastBody,
        pctComplete: streamPctComplete(lastBody, 100),
        elapsedMs: Date.now() - start,
      };
    }

    const remaining = maxMs - (Date.now() - start);
    if (remaining <= 0) break;
    await sleep(Math.min(pollMs, remaining));
  }

  return {
    ready: isCloudflareStreamReady(lastBody),
    body: lastBody,
    pctComplete: streamPctComplete(lastBody, null),
    elapsedMs: Date.now() - start,
  };
}
